import * as B from '@blueprintjs/core';
import React from 'react';

import {AlertPolicy} from 'app/modules/Remote/Feature';
import {alphanumericSort} from 'app/utils/featureUtils';
import {parseLayerKey} from 'app/utils/layerUtils';

import {
  BulkAction,
  BulkMode,
  BulkSkip,
  PortfolioPlan,
  SkipReason,
  computeBulkPlan,
  getAffectedAlertPolicies,
  getLayerSkipReason,
} from './bulkEnroll';
import cs from './BulkLayerEnrollmentModal.module.scss';
import {LayerDataset, LibraryDataset} from './LibraryTypes';

export interface BulkPortfolio {
  id: string;
  name: string;
}

// `short` labels a layer row in the picker, `heading` a group on the review
// step. A reason implies its mode, so one table covers both.
const SKIP_LABELS: Record<SkipReason, {short: string; heading: string}> = {
  'already-enabled': {
    short: 'Already enrolled',
    heading: 'Already enrolled — no processing needed',
  },
  'not-supported': {short: 'Not available', heading: 'Not available for the selected portfolios'},
  'not-enrolled': {short: 'Not enrolled', heading: 'Not currently enrolled — nothing to remove'},
};

const layerSkipLabel = (reason: SkipReason, portfolioCount: number) =>
  portfolioCount > 1
    ? `${SKIP_LABELS[reason].short} in selected portfolios`
    : SKIP_LABELS[reason].short;

// One review row per layer, naming the portfolios the verdict applies to.
interface ReviewRow {
  layerName: string;
  portfolioNames: string[];
}

const MAX_REVIEW_ROWS = 20;
const MAX_PORTFOLIO_NAMES = 5;

// Names arrive sorted from `groupRowsByLayer`.
const formatPortfolioNames = (names: string[]) => {
  if (names.length <= MAX_PORTFOLIO_NAMES) {
    return names.join(', ');
  }
  const shown = names.slice(0, MAX_PORTFOLIO_NAMES).join(', ');
  return `${shown} and ${names.length - MAX_PORTFOLIO_NAMES} more`;
};

// A verdict lands per (portfolio, layer) pair, so collapse to one row per layer
// and name the portfolios it applies to — otherwise the layer name repeats.
const groupRowsByLayer = (
  actions: BulkAction[],
  portfolioNameById: Map<string, string>
): ReviewRow[] => {
  const rowsByLayerKey = new Map<string, ReviewRow>();
  actions.forEach(({layerKey, layerName, projectId}) => {
    const row = rowsByLayerKey.get(layerKey) ?? {layerName, portfolioNames: []};
    rowsByLayerKey.set(layerKey, row);
    row.portfolioNames.push(portfolioNameById.get(projectId) ?? projectId);
  });

  const rows = [...rowsByLayerKey.values()];
  rows.forEach((row) => row.portfolioNames.sort((a, b) => alphanumericSort(a, b)));
  return rows.sort((a, b) => alphanumericSort(a.layerName, b.layerName));
};

const requireSelection = (count: number, noun: string) => ({
  disabled: count === 0,
  tooltipContent: count === 0 ? `Select at least one ${noun}` : undefined,
});

// A layer that can't be bulk-enrolled and why (used to disable its row).
function layerDisabledReason(
  layer: LibraryDataset<LayerDataset>,
  subscribedPremiumSourceIds: string[]
): string | undefined {
  if (layer.restricted) {
    return 'Standard and Enterprise only';
  }
  const {sourceId} = parseLayerKey(layer.layerKey);
  if (layer.premium && !subscribedPremiumSourceIds.includes(sourceId)) {
    return 'Purchase first';
  }
  return undefined;
}

export const BulkLayerEnrollmentModal: React.FunctionComponent<{
  isOpen: boolean;
  onClose: () => void;
  layers: LibraryDataset<LayerDataset>[];
  portfolios: BulkPortfolio[];
  projectIdToFcId: Record<string, number>;
  subscribedPremiumSourceIds: string[];
  alertPolicies: AlertPolicy[];
  alertPoliciesLoading: boolean;
  alertPoliciesError?: boolean;
  canEdit: boolean;
  onOpenCheckout: (layerKey: string) => void;
  onSubmit: (mode: BulkMode, portfolioPlans: PortfolioPlan[]) => Promise<void>;
}> = ({
  isOpen,
  onClose,
  layers,
  portfolios,
  projectIdToFcId,
  subscribedPremiumSourceIds,
  alertPolicies,
  alertPoliciesLoading,
  alertPoliciesError = false,
  canEdit,
  onOpenCheckout,
  onSubmit,
}) => {
  const [mode, setMode] = React.useState<BulkMode>('add');
  const [selectedLayerKeys, setSelectedLayerKeys] = React.useState<Set<string>>(new Set());
  const [selectedProjectIds, setSelectedProjectIds] = React.useState<Set<string>>(new Set());
  const [layerSearch, setLayerSearch] = React.useState('');
  const [confirmedPolicySignature, setConfirmedPolicySignature] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const reset = React.useCallback(() => {
    setMode('add');
    setSelectedLayerKeys(new Set());
    setSelectedProjectIds(new Set());
    setLayerSearch('');
    setConfirmedPolicySignature('');
    setSubmitting(false);
  }, []);

  // Only real, enrollable library layers (drop preview / custom-action entries).
  const selectableLayers = React.useMemo(
    () => layers.filter((l) => !l.libraryAction).sort((a, b) => alphanumericSort(a.name, b.name)),
    [layers]
  );

  const layersByKey = React.useMemo(() => {
    const map = new Map<string, LibraryDataset<LayerDataset>>();
    layers.forEach((l) => map.set(l.layerKey, l));
    return map;
  }, [layers]);

  const filteredLayers = React.useMemo(() => {
    const q = layerSearch.trim().toLowerCase();
    if (!q) return selectableLayers;
    return selectableLayers.filter(
      (l) => l.name.toLowerCase().includes(q) || l.source.toLowerCase().includes(q)
    );
  }, [selectableLayers, layerSearch]);

  // Group the (already name-sorted) layers under their source, then order the
  // source groups alphabetically so the picker reads "Sentinel-2 → its layers,
  // next source → its layers", matching how the pipeline batches by source.
  const layerGroups = React.useMemo(() => {
    const bySource = new Map<string, LibraryDataset<LayerDataset>[]>();
    filteredLayers.forEach((layer) => {
      const group = bySource.get(layer.source) ?? [];
      group.push(layer);
      bySource.set(layer.source, group);
    });
    return [...bySource.entries()]
      .sort(([a], [b]) => alphanumericSort(a, b))
      .map(([source, sourceLayers]) => ({source, layers: sourceLayers}));
  }, [filteredLayers]);

  const sortedPortfolios = React.useMemo(
    () => [...portfolios].sort((a, b) => alphanumericSort(a.name, b.name)),
    [portfolios]
  );

  // Why each layer is out of play: entitlement first, then what the chosen
  // portfolios leave to do. Portfolios are picked before layers, so a layer that
  // would be skipped everywhere can be ruled out here instead of at review.
  const skipReasonByLayerKey = React.useMemo(() => {
    const selectedFcIds = [...selectedProjectIds]
      .map((projectId) => projectIdToFcId[projectId])
      .filter((fcId) => fcId !== undefined);

    const reasons = new Map<string, string>();
    selectableLayers.forEach((layer) => {
      const reason = layerDisabledReason(layer, subscribedPremiumSourceIds);
      if (reason) {
        reasons.set(layer.layerKey, reason);
        return;
      }
      const skipReason = getLayerSkipReason(mode, layer, selectedFcIds);
      if (skipReason) {
        reasons.set(layer.layerKey, layerSkipLabel(skipReason, selectedFcIds.length));
      }
    });
    return reasons;
  }, [selectableLayers, subscribedPremiumSourceIds, mode, selectedProjectIds, projectIdToFcId]);

  // Revisiting the portfolio step can turn an already-picked layer into a no-op,
  // so act on what's still actionable rather than pruning the checkbox state.
  const activeLayerKeys = React.useMemo(
    () => new Set([...selectedLayerKeys].filter((key) => !skipReasonByLayerKey.has(key))),
    [selectedLayerKeys, skipReasonByLayerKey]
  );

  const plan = React.useMemo(
    () =>
      computeBulkPlan(
        mode,
        [...activeLayerKeys],
        [...selectedProjectIds],
        layersByKey,
        projectIdToFcId
      ),
    [mode, activeLayerKeys, selectedProjectIds, layersByKey, projectIdToFcId]
  );

  const affectedAlertPolicies = React.useMemo(
    () => (mode === 'remove' ? getAffectedAlertPolicies(plan.portfolioPlans, alertPolicies) : []),
    [mode, plan.portfolioPlans, alertPolicies]
  );

  const toggle = (set: Set<string>, id: string): Set<string> => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  };

  // Bulk-toggle the enrollable layers within one source group.
  const setSourceSelection = (groupLayers: LibraryDataset<LayerDataset>[], selected: boolean) => {
    setSelectedLayerKeys((s) => {
      const next = new Set(s);
      groupLayers.forEach((l) => {
        if (skipReasonByLayerKey.has(l.layerKey)) return;
        selected ? next.add(l.layerKey) : next.delete(l.layerKey);
      });
      return next;
    });
  };

  // Until the lookout query lands, `alertPolicies` is empty and indistinguishable
  // from "no lookouts", so removals would skip the warning entirely. Waiting is
  // brief, so block; a failed query may never resolve, so ask instead — the risk
  // is the same either way, and a dead end would leave no way to remove a layer.
  const alertsUnknown = mode === 'remove' && alertPoliciesLoading;
  const alertsFailed = mode === 'remove' && !alertPoliciesLoading && alertPoliciesError;

  // The confirmation records which lookouts it covered, so widening the
  // selection afterwards asks again instead of riding on the old consent. A
  // failed check gets its own signature: nothing about the layers narrows the
  // risk, so the consent is to removing blind.
  const affectedPolicySignature = React.useMemo(
    () =>
      alertsFailed
        ? 'lookout-check-failed'
        : affectedAlertPolicies
            .map((policy) => policy.id)
            .sort()
            .join(','),
    [alertsFailed, affectedAlertPolicies]
  );
  const alertConfirmed = confirmedPolicySignature === affectedPolicySignature;

  // Visited steps stay clickable in the left nav, so the layer picker has to cope
  // with the portfolio selection being emptied out from under it.
  const hasPortfolios = selectedProjectIds.size > 0;

  const needsAlertConfirmation = alertsFailed || affectedAlertPolicies.length > 0;
  const alertBlocking = alertsUnknown || (needsAlertConfirmation && !alertConfirmed);
  const canEnroll = canEdit && plan.applied.length > 0 && !alertBlocking && !submitting;

  const handleEnroll = async () => {
    setSubmitting(true);
    try {
      await onSubmit(mode, plan.portfolioPlans);
      onClose();
    } catch {
      // onSubmit reports and alerts; leave the dialog open so they can retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <B.MultistepDialog
      className={cs.bulkModal}
      isOpen={isOpen}
      title={mode === 'add' ? 'Bulk add layers' : 'Bulk remove layers'}
      onClose={onClose}
      onOpening={reset}
      canEscapeKeyClose={!submitting}
      canOutsideClickClose={false}
      isCloseButtonShown={!submitting}
      backButtonProps={{text: 'Back'}}
      finalButtonProps={{
        text: mode === 'add' ? 'Enable layers' : 'Remove layers',
        intent: mode === 'remove' ? 'danger' : 'primary',
        disabled: !canEnroll,
        loading: submitting,
        onClick: handleEnroll,
      }}
    >
      <B.DialogStep
        id="mode"
        title="Choose action"
        panel={
          <div className={cs.dialogPanel}>
            <B.FormGroup label="What do you want to do?">
              <B.RadioGroup
                selectedValue={mode}
                onChange={(e) => setMode(e.currentTarget.value as BulkMode)}
              >
                <B.Radio large value="add" label="Add layers to portfolios" />
                <B.Radio large value="remove" label="Remove layers from portfolios" />
              </B.RadioGroup>
            </B.FormGroup>
          </div>
        }
      />

      <B.DialogStep
        id="portfolios"
        title="Select portfolios"
        nextButtonProps={requireSelection(selectedProjectIds.size, 'portfolio')}
        panel={
          <div className={cs.selectionPanel}>
            <div className={cs.selectionToolbar}>
              <B.ButtonGroup minimal>
                <B.Button
                  small
                  text="Select all"
                  onClick={() => setSelectedProjectIds(new Set(portfolios.map((p) => p.id)))}
                  active={portfolios.length > 0 && selectedProjectIds.size === portfolios.length}
                />
                <B.Button
                  small
                  text="Select none"
                  onClick={() => setSelectedProjectIds(new Set())}
                  active={selectedProjectIds.size === 0}
                />
              </B.ButtonGroup>
              <span className={cs.rowMeta}>{selectedProjectIds.size} selected</span>
            </div>
            <div className={cs.list}>
              {sortedPortfolios.length === 0 && (
                <span className={cs.rowMeta}>This organization has no portfolios yet.</span>
              )}
              {sortedPortfolios.map((p) => (
                <B.Checkbox
                  key={p.id}
                  checked={selectedProjectIds.has(p.id)}
                  label={p.name}
                  onChange={() => setSelectedProjectIds((s) => toggle(s, p.id))}
                />
              ))}
            </div>
          </div>
        }
      />

      <B.DialogStep
        id="layers"
        title="Select layers"
        nextButtonProps={
          hasPortfolios
            ? requireSelection(activeLayerKeys.size, 'layer')
            : requireSelection(0, 'portfolio')
        }
        panel={
          <div className={cs.selectionPanel}>
            <div className={cs.selectionToolbar}>
              <B.InputGroup
                className={cs.layerSearch}
                leftIcon="search"
                placeholder="Search layers…"
                aria-label="Search layers"
                value={layerSearch}
                round
                onChange={(e) => setLayerSearch(e.currentTarget.value)}
              />
              <span className={cs.rowMeta}>{activeLayerKeys.size} selected</span>
            </div>
            <div className={cs.list}>
              {!hasPortfolios && (
                <B.Callout intent="warning" icon="arrow-left">
                  Select at least one portfolio first — which layers apply depends on it.
                </B.Callout>
              )}
              {hasPortfolios && layerGroups.length === 0 && (
                <span className={cs.rowMeta}>No layers match your search.</span>
              )}
              {hasPortfolios &&
                layerGroups.map(({source, layers: sourceLayers}) => {
                  const enrollable = sourceLayers.filter(
                    (l) => !skipReasonByLayerKey.has(l.layerKey)
                  );
                  const selectedInSource = enrollable.filter((l) =>
                    activeLayerKeys.has(l.layerKey)
                  ).length;
                  const allSelected =
                    enrollable.length > 0 && selectedInSource === enrollable.length;

                  return (
                    <div key={source} className={cs.sourceGroup} role="group" aria-label={source}>
                      <div className={cs.sourceHeader}>
                        <B.Checkbox
                          className={cs.sourceHeaderCheckbox}
                          checked={allSelected}
                          indeterminate={selectedInSource > 0 && !allSelected}
                          disabled={enrollable.length === 0}
                          onChange={() => setSourceSelection(enrollable, !allSelected)}
                          labelElement={<span className={cs.sourceHeaderLabel}>{source}</span>}
                        />
                      </div>
                      {sourceLayers.map((layer) => {
                        const disabledReason = skipReasonByLayerKey.get(layer.layerKey);
                        return (
                          <div key={layer.layerKey} className={cs.row}>
                            <B.Checkbox
                              className={cs.layerCheckbox}
                              checked={activeLayerKeys.has(layer.layerKey)}
                              disabled={!!disabledReason}
                              onChange={() =>
                                setSelectedLayerKeys((s) => toggle(s, layer.layerKey))
                              }
                              label={layer.name}
                            />
                            {disabledReason &&
                              (disabledReason === 'Purchase first' ? (
                                <B.Button
                                  minimal
                                  small
                                  icon="shopping-cart"
                                  text="Purchase first"
                                  onClick={() => onOpenCheckout(layer.layerKey)}
                                />
                              ) : (
                                <span className={cs.rowMeta}>{disabledReason}</span>
                              ))}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
            </div>
          </div>
        }
      />

      <B.DialogStep
        id="review"
        title="Review"
        panel={
          <ReviewStep
            mode={mode}
            plan={plan}
            layerCount={activeLayerKeys.size}
            portfolioCount={selectedProjectIds.size}
            affectedAlertPolicies={affectedAlertPolicies}
            alertsUnknown={alertsUnknown}
            alertsFailed={alertsFailed}
            alertConfirmed={alertConfirmed}
            onConfirmAlerts={(confirmed) =>
              setConfirmedPolicySignature(confirmed ? affectedPolicySignature : '')
            }
            canEdit={canEdit}
            portfolios={portfolios}
            submitting={submitting}
          />
        }
      />
    </B.MultistepDialog>
  );
};

const ReviewStep: React.FunctionComponent<{
  mode: BulkMode;
  plan: ReturnType<typeof computeBulkPlan>;
  layerCount: number;
  portfolioCount: number;
  affectedAlertPolicies: AlertPolicy[];
  alertsUnknown: boolean;
  alertsFailed: boolean;
  alertConfirmed: boolean;
  onConfirmAlerts: (confirmed: boolean) => void;
  canEdit: boolean;
  portfolios: BulkPortfolio[];
  submitting: boolean;
}> = ({
  mode,
  plan,
  layerCount,
  portfolioCount,
  affectedAlertPolicies,
  alertsUnknown,
  alertsFailed,
  alertConfirmed,
  onConfirmAlerts,
  canEdit,
  portfolios,
  submitting,
}) => {
  const verb = mode === 'add' ? 'Enabling' : 'Removing';
  const actionCount = plan.applied.length;

  const portfolioNameById = React.useMemo(
    () => new Map(portfolios.map((p) => [p.id, p.name])),
    [portfolios]
  );

  const appliedRows = React.useMemo(
    () => groupRowsByLayer(plan.applied, portfolioNameById),
    [plan.applied, portfolioNameById]
  );

  const skipRowsByReason = React.useMemo(() => {
    const skipsByReason = new Map<SkipReason, BulkSkip[]>();
    plan.skipped.forEach((skip) => {
      const skips = skipsByReason.get(skip.reason) ?? [];
      skips.push(skip);
      skipsByReason.set(skip.reason, skips);
    });

    return [...skipsByReason.entries()].map(([reason, skips]) => ({
      reason,
      rows: groupRowsByLayer(skips, portfolioNameById),
    }));
  }, [plan.skipped, portfolioNameById]);

  // Enrollment updates the layers optimistically, so mid-submit the plan sees its
  // own work as done and every verdict here flips to "nothing to process". Show
  // progress until the request settles — and on failure, the recomputed plan.
  if (submitting) {
    return (
      <div className={cs.dialogPanel}>
        {/* Blueprint's icon slot positions `.bp5-icon` absolutely and passes
            non-icon elements straight through, so the spinner goes inline. */}
        <B.Callout className={cs.callout} intent="primary" icon={null}>
          <span className={cs.submittingRow}>
            <B.Spinner size={16} />
            {verb} layers…
          </span>
        </B.Callout>
      </div>
    );
  }

  return (
    <div className={cs.dialogPanel}>
      <B.Callout
        className={cs.callout}
        intent={actionCount > 0 ? 'success' : 'warning'}
        icon={actionCount > 0 ? 'tick-circle' : 'warning-sign'}
      >
        {verb} {layerCount} layer{layerCount === 1 ? '' : 's'} across {portfolioCount} portfolio
        {portfolioCount === 1 ? '' : 's'}.{' '}
        {actionCount > 0 ? (
          <>
            <b>{actionCount}</b> update{actionCount === 1 ? '' : 's'} will be processed
            {plan.skipped.length > 0 && (
              <>
                {' '}
                and <b>{plan.skipped.length}</b> skipped
              </>
            )}
            .
          </>
        ) : layerCount === 0 || portfolioCount === 0 ? (
          <>Nothing to process — pick at least one portfolio and one layer.</>
        ) : (
          <>Nothing to process — every selection is already in the desired state.</>
        )}
      </B.Callout>

      {!canEdit && (
        <B.Callout className={cs.callout} intent="warning" icon="lock">
          Please reach out to an admin or member on your team to update layers.
        </B.Callout>
      )}

      {alertsUnknown && (
        <B.Callout className={cs.callout} intent="warning" icon="time">
          Checking which lookouts use these layers…
        </B.Callout>
      )}

      {alertsFailed && (
        <B.Callout className={cs.callout} intent="danger" icon="error">
          <p>
            We couldn’t check which lookouts use these layers. Removing them may stop lookouts that
            depend on them.
          </p>
          <B.Checkbox
            checked={alertConfirmed}
            label="I understand — remove these layers anyway"
            onChange={(e) => onConfirmAlerts(e.currentTarget.checked)}
          />
        </B.Callout>
      )}

      {!alertsUnknown && !alertsFailed && mode === 'remove' && affectedAlertPolicies.length > 0 && (
        <B.Callout className={cs.callout} intent="danger" icon="warning-sign">
          <p>
            This will affect <b>{affectedAlertPolicies.length}</b> lookout
            {affectedAlertPolicies.length === 1 ? '' : 's'} that use these layers.
          </p>
          <B.Checkbox
            checked={alertConfirmed}
            label="I understand — remove these layers anyway"
            onChange={(e) => onConfirmAlerts(e.currentTarget.checked)}
          />
        </B.Callout>
      )}

      <ReviewGroup
        heading={mode === 'add' ? 'Will be added' : 'Will be removed'}
        rows={appliedRows}
      />

      {skipRowsByReason.map(({reason, rows}) => (
        <ReviewGroup key={reason} heading={SKIP_LABELS[reason].heading} rows={rows} />
      ))}
    </div>
  );
};

const ReviewGroup: React.FunctionComponent<{heading: string; rows: ReviewRow[]}> = ({
  heading,
  rows,
}) => {
  if (rows.length === 0) {
    return null;
  }
  const overflow = rows.length - MAX_REVIEW_ROWS;
  return (
    <div className={cs.reviewGroup}>
      <b>{heading}</b>
      <ul className={cs.reviewList}>
        {rows.slice(0, MAX_REVIEW_ROWS).map((row, i) => (
          <li key={i}>
            {row.layerName} — {formatPortfolioNames(row.portfolioNames)}
          </li>
        ))}
        {overflow > 0 && (
          <li>
            …and {overflow} more layer{overflow === 1 ? '' : 's'}
          </li>
        )}
      </ul>
    </div>
  );
};
